import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectIsUnassigned2dPhotosPanelVisible } from "@/store/ui/ui-selectors";
import { setIsUnassigned2dPhotosPanelVisible } from "@/store/ui/ui-slice";
import {
  ArrowDownIcon,
  FaroText,
  grey,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { Box, Stack } from "@mui/system";
import { useCallback, type JSX } from "react";
import { selectUnassigned2dPhotos } from "./unassigned-2d-photos-selectors";

/**
 * @returns Button that shows and hides the unassigned 2d photos tab
 */
export function Unassigned2dPhotosButton(): JSX.Element | null {
  const unassigned2dPhotos = useAppSelector(selectUnassigned2dPhotos());
  const unassigned2dPhotosFeatureEnabled = useAppSelector(
    selectHasFeature(Features.Unassigned2dPhotos),
  );
  const dispatch = useAppDispatch();
  const isPanelVisible = useAppSelector(selectIsUnassigned2dPhotosPanelVisible);
  const togglePanelVisibility = useCallback(() => {
    dispatch(setIsUnassigned2dPhotosPanelVisible(!isPanelVisible));
  }, [dispatch, isPanelVisible]);

  if (unassigned2dPhotos.length === 0 || !unassigned2dPhotosFeatureEnabled) {
    return null;
  }

  return (
    <Box
      component="div"
      onClick={togglePanelVisibility}
      sx={{
        mx: 2,
        mt: 1,
        borderColor: `${grey[200]}`,
        borderStyle: "solid",
        borderWidth: 1,
        borderRadius: 1,
        padding: 1,
        backgroundColor: `${grey[50]}`,
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={1}>
          <ElementIcon
            icon={ElementIconType.ImageIcon}
            sx={{
              width: "18px",
              height: "18px",
              lineHeight: 0,
            }}
          />
          <FaroText variant="bodyM" color="inherit" noWrap>
            Unassigned 2D Photos (
            <TranslateVar name="numberOfUnassigned2dPhotos">
              {unassigned2dPhotos.length}
            </TranslateVar>
            )
          </FaroText>
        </Stack>
        <ArrowDownIcon
          sx={{
            height: 18,
            width: 18,
            transform: isPanelVisible ? "rotate(90deg)" : "rotate(-90deg)",
            transition: "transform 0.1s linear",
          }}
        />
      </Stack>
    </Box>
  );
}
